import axios from 'axios';

class MicrosoftGraphService {
  constructor() {
    this.baseURL = 'https://graph.microsoft.com/v1.0';
    this.accessToken = null;
  }

  // Initialize with access token
  setAccessToken(token) {
    this.accessToken = token;
  }

  // Get headers for API requests
  getHeaders() {
    return {
      'Authorization': `Bearer ${this.accessToken}`,
      'Content-Type': 'application/json'
    };
  }

  // Upload document to OneDrive
  async uploadToOneDrive(fileName, fileContent, folderPath = '/Documents') {
    try {
      const uploadUrl = `${this.baseURL}/me/drive/root:${folderPath}/${fileName}:/content`;
      
      const response = await axios.put(uploadUrl, fileContent, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }
      });

      return {
        success: true,
        fileId: response.data.id,
        webUrl: response.data.webUrl,
        downloadUrl: response.data['@microsoft.graph.downloadUrl']
      };
    } catch (error) {
      console.error('Error uploading to OneDrive:', error);
      return { success: false, error: error.message };
    }
  }

  // Create sharing link for document — org-only by default (not
  // 'anonymous'/public): a clinical-trial document link shouldn't be
  // openable by anyone with the URL (security review SEC-009).
  async createSharingLink(fileId, type = 'view', scope = 'organization') {
    try {
      const response = await axios.post(
        `${this.baseURL}/me/drive/items/${fileId}/createLink`,
        {
          type: type,
          scope: scope
        },
        { headers: this.getHeaders() }
      );

      return {
        success: true,
        sharingUrl: response.data.link.webUrl
      };
    } catch (error) {
      console.error('Error creating sharing link:', error);
      return { success: false, error: error.message };
    }
  }

  // Open document in Word Online
  async openInWordOnline(fileId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/me/drive/items/${fileId}/workbook/worksheets`,
        { headers: this.getHeaders() }
      );

      const webUrl = `https://word.office.com/we/wordeditorframe.aspx?ui=en-US&rs=en-US&WOPISrc=https://graph.microsoft.com/v1.0/me/drive/items/${fileId}`;
      
      return {
        success: true,
        wordOnlineUrl: webUrl
      };
    } catch (error) {
      console.error('Error opening in Word Online:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user's OneDrive files
  async getOneDriveFiles(folderPath = '/Documents') {
    try {
      const response = await axios.get(
        `${this.baseURL}/me/drive/root:${folderPath}:/children`,
        { headers: this.getHeaders() }
      );

      return {
        success: true,
        files: response.data.value
      };
    } catch (error) {
      console.error('Error getting OneDrive files:', error);
      return { success: false, error: error.message };
    }
  }

  // Update document content
  async updateDocument(fileId, newContent) {
    try {
      const response = await axios.put(
        `${this.baseURL}/me/drive/items/${fileId}/content`,
        newContent,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          }
        }
      );

      return {
        success: true,
        updatedFile: response.data
      };
    } catch (error) {
      console.error('Error updating document:', error);
      return { success: false, error: error.message };
    }
  }

  // Get document metadata
  async getDocumentInfo(fileId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/me/drive/items/${fileId}`,
        { headers: this.getHeaders() }
      );

      return {
        success: true,
        document: response.data
      };
    } catch (error) {
      console.error('Error getting document info:', error);
      return { success: false, error: error.message };
    }
  }

  
  // Download file as PDF (Graph transforms on-the-fly)
  async downloadAsPdf(fileId) {
    try {
      const url = `${this.baseURL}/me/drive/items/${fileId}/content?format=pdf`;
      const response = await axios.get(url, {
        headers: this.getHeaders(),
        responseType: 'blob'
      });
      return { success: true, blob: response.data };
    } catch (error) {
      console.error('Error downloading as PDF:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete file from OneDrive
  async deleteFile(fileId) {
    try {
      await axios.delete(`${this.baseURL}/me/drive/items/${fileId}`, { headers: this.getHeaders() });
      return { success: true };
    } catch (error) {
      console.warn('Error deleting OneDrive file (continuing):', error?.message);
      return { success: false, error: error.message };
    }
  }
}

export default new MicrosoftGraphService();



